import { useState, useEffect } from 'react';

/**
 * @param {Object} options - Configuration options
 * @param {boolean} [options.isStorybook] - Explicitly set Storybook mode (auto-detected if not provided)
 * @param {any} [options.mockData] - Custom mock data to use
 * @param {string} [options.tourKey='hasSeenTour'] - localStorage key to check for tour completion
 * @returns {Object} - { showDummyData, isInStorybook, firstLogin }
 */

/**
 * Dispatch a synthetic storage event so hooks listening for tour status
 * can update immediately without relying on polling.
 *
 * @param {string} [tourKey='hasSeenTour']
 */

export const notifyTourStatusChange = (tourKey = 'hasSeenTour') => {
    if (typeof window === 'undefined') return;

    try {
        const storageEvent = new StorageEvent('storage', {
            key: tourKey,
            newValue: localStorage.getItem(tourKey),
            storageArea: localStorage,
        });
        window.dispatchEvent(storageEvent);
    } catch (e) {
        // Fallback for environments without StorageEvent constructor support
        window.dispatchEvent(new Event('storage'));
    }
};

export const useDummyData = ({ isStorybook, mockData, tourKey = 'hasSeenTour' } = {}) => {
    // Auto-detect Storybook environment
    const detectedStorybook =
        isStorybook !== undefined
            ? isStorybook
            : (typeof window !== 'undefined' && window.location?.pathname?.includes('storybook')) ||
            process.env.STORYBOOK === 'true';

    // Check if first-time user (hasn't seen tour) - use state to react to changes
    const [firstLogin, setFirstLogin] = useState(() => !localStorage.getItem(tourKey));

    // Listen for localStorage changes when tour is skipped/completed
    useEffect(() => {
        const checkTourStatus = () => {
            const hasSeenTour = localStorage.getItem(tourKey);
            setFirstLogin(!hasSeenTour);
        };

        // Check on mount
        checkTourStatus();

        // Listen for storage events (works for same-tab changes via synthetic events or other tabs)
        const handleStorageChange = (e) => {
            if (e.key === tourKey || !e.key) {
                checkTourStatus();
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [tourKey]);

    const showDummyData = detectedStorybook || Boolean(mockData) || firstLogin;

    return {
        showDummyData,
        isInStorybook: detectedStorybook,
        firstLogin,
    };
};

export default useDummyData;

